import { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { uploadFile, validateFile, FileReadError, UploadProgress, UploadResult } from '../../services/uploadService';
import { useLanguage } from '../../i18n/LanguageContext';

interface UploadedFile {
  mediaId: string;
  storageKey: string;
  fileName: string;
  downloadUrl: string;
}

interface FileDropZoneProps {
  mediaType: string;
  token: string;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  label?: string;
  sublabel?: string;
  files?: UploadedFile[];
  onUploadComplete?: (file: UploadedFile) => void;
  onRemove?: (mediaId: string) => void;
  uploadFn?: (file: File, mediaType: string, token: string, onProgress?: (progress: UploadProgress) => void) => Promise<UploadResult>;
}

function CloudUploadIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M28.33 16.67C27.26 11.87 23.07 8.33 18.33 8.33C14.4 8.33 10.97 10.6 9.3 13.87C5.37 14.3 2.5 17.63 2.5 21.67C2.5 25.98 6.02 29.5 10.33 29.5H27.5C31.17 29.5 34.17 26.5 34.17 22.83C34.17 19.33 31.43 16.5 28.33 16.67Z" fill="#4f46e5" fillOpacity="0.12"/>
      <path d="M20 18.33V26.67" stroke="#4f46e5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M23.33 21.67L20 18.33L16.67 21.67" stroke="#4f46e5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function FileDropZone({
  mediaType,
  token,
  accept = 'image/jpeg,image/png',
  multiple = false,
  maxFiles = 1,
  label,
  sublabel,
  files = [],
  onUploadComplete,
  onRemove,
  uploadFn,
}: FileDropZoneProps) {
  const { t } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayLabel = label || t('fileDropZone.defaultLabel');
  const displaySublabel = sublabel || '';

  const handleFiles = async (fileList: FileList) => {
    setError(null);
    const allFiles = Array.from(fileList);
    const remainingSlots = Math.max(0, maxFiles - files.length);
    const filesToUpload = allFiles.slice(0, remainingSlots);
    const errors: string[] = [];

    if (allFiles.length > filesToUpload.length) {
      errors.push(`${t('fileDropZone.maxFilesExceeded')} (${maxFiles})`);
    }

    for (const file of filesToUpload) {
      const validationError = validateFile(file, mediaType);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
        continue;
      }

      setUploading(true);
      setProgress(null);
      try {
        const doUpload = uploadFn || uploadFile;
        const result = await doUpload(file, mediaType, token, setProgress);
        onUploadComplete?.({
          mediaId: result.mediaId,
          storageKey: result.storageKey,
          fileName: file.name,
          downloadUrl: result.downloadUrl,
        });
      } catch (err) {
        const reason = err instanceof Error ? err.message : t('common.uploadFailed');
        // FileReadError already names the file
        errors.push(err instanceof FileReadError ? reason : `${file.name}: ${reason}`);
      } finally {
        setUploading(false);
        setProgress(null);
      }
    }

    if (errors.length) setError(errors.join('\n'));
  };

  const onDragOver = (e: DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = (e: DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setIsDragging(false); handleFiles(e.dataTransfer.files); };
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(e.target.files);
    // Reset so the same file can be re-selected after a failure
    e.target.value = '';
  };

  const canUpload = files.length < maxFiles;

  return (
    <div style={{ marginBottom: '1rem' }}>
      {canUpload && (
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? '#4f46e5' : '#94a3b8'}`,
            borderRadius: '5px',
            padding: '1.25rem 2rem',
            textAlign: 'center',
            cursor: uploading ? 'wait' : 'pointer',
            background: isDragging ? '#effafa' : '#fff',
            transition: 'all 0.2s',
            minHeight: '104px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.25rem',
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={onChange}
            style={{ display: 'none' }}
          />
          {uploading ? (
            <div>
              <p style={{ margin: 0, color: '#505050' }}>{t('fileDropZone.uploading')}</p>
              {progress && (
                <div style={{ marginTop: '0.5rem', width: '200px' }}>
                  <div style={{ background: '#e0e0e0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                    <div style={{ background: '#4f46e5', height: '100%', width: `${progress.percentage}%`, transition: 'width 0.2s' }} />
                  </div>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#a3a3a3' }}>{progress.percentage}%</p>
                </div>
              )}
            </div>
          ) : (
            <>
              <CloudUploadIcon />
              <p style={{ margin: 0, color: '#505050', fontSize: '0.875rem' }}>{displayLabel}</p>
              {displaySublabel && (
                <p style={{ margin: 0, color: '#505050', fontSize: '0.875rem' }}>{displaySublabel}</p>
              )}
            </>
          )}
        </div>
      )}

      {error && <p style={{ color: '#c62828', fontSize: '0.85rem', marginTop: '0.5rem', whiteSpace: 'pre-line' }}>{error}</p>}

      {files.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          {files.map((f) => (
            <div
              key={f.mediaId}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5rem 0.75rem',
                background: '#f5f5f5',
                borderRadius: '4px',
                marginBottom: '0.5rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                {f.downloadUrl ? (
                  <img
                    src={f.downloadUrl}
                    alt={f.fileName}
                    style={{ width: 48, height: 48, borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : null}
                <span style={{ fontSize: '0.9rem', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fileName}</span>
              </div>
              {onRemove && (
                <button
                  onClick={() => onRemove(f.mediaId)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#c62828',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    padding: '0.25rem 0.5rem',
                    flexShrink: 0,
                  }}
                >
                  {t('common.remove')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
